import { useState, useRef, useEffect } from "react";

import {
  AttachFileOutlined,
  EditOutlined,
  FolderOutlined,
  ImageOutlined,
} from "@mui/icons-material";
import { orange } from "@mui/material/colors";
import { isEmpty } from "lodash";

import { CloseRounded } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  Button,
} from "@mui/material";
import { isNil } from "lodash";
import SelectFileAssignmentInput from "../../../../components/inputs/SelectFileAssignmentInput";
import BasicModal from "../../../../components/modals/BasicModal";
import Txt from "../../../../components/typography/Txt";
import Flx from "../../../../components/layout/Flx";
import InputWrapper from "../../../../components/inputs/shared/InputWrapper";

// Updated component with editable file names
const RenderedPreviewUploadedFiles = ({
  files,
  onDelete,
  onUpdateFileName,
  onUpdateDocGroupType,
}) => {
  if (isEmpty(files)) {
    return null;
  }

  return files.map((fileObj) => (
    <EditableCommentAttachmentChip
      key={fileObj.id}
      background={orange[50]}
      color={orange[800]}
      icon={
        fileObj.isImage ? (
          <ImageOutlined className="thin" />
        ) : (
          <AttachFileOutlined className="thin" />
        )
      }
      onDelete={() => onDelete(fileObj.id)}
      imagePreview={fileObj.isImage ? fileObj.preview : null}
      fileName={fileObj.file_display_name}
      originalFileName={fileObj.name} // Pass the original file name
      docGroup={fileObj.docGroup}
      docType={fileObj.docType}
      onUpdateFileName={(newName) => onUpdateFileName(fileObj.id, newName)}
      onUpdateDocGroupType={(newGroup, newType) =>
        onUpdateDocGroupType &&
        onUpdateDocGroupType(fileObj.id, newGroup, newType)
      }
      enableDocGroupTypeEdit={true}
    />
  ));
};

const EditableCommentAttachmentChip = ({
  fileName,
  icon,
  onDelete,
  background,
  color,
  imagePreview,
  onUpdateFileName,
  originalFileName, // Pass the original file.name to preserve extension
  docGroup,
  docType,
  onUpdateDocGroupType, // New prop for updating doc group/type
  enableDocGroupTypeEdit = true, // Optional prop to enable/disable this feature
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(fileName);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempDocGroup, setTempDocGroup] = useState(docGroup);
  const [tempDocType, setTempDocType] = useState(docType);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Access the actual input element inside TextField
      const inputElement = inputRef.current.querySelector("input");
      if (inputElement) {
        inputElement.focus();
        // Select all text
        inputElement.select();
      }
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(fileName);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue) {
      // Preserve the original file extension
      let finalValue = trimmedValue;

      if (originalFileName) {
        const originalExtension = originalFileName
          .split(".")
          .pop()
          .toLowerCase();
        const newValueWithoutExt = trimmedValue.replace(/\.[^/.]+$/, "");
        finalValue = `${newValueWithoutExt}.${originalExtension}`;
      }

      if (finalValue !== fileName) {
        onUpdateFileName(finalValue);
      }
      setEditValue(finalValue);
    } else {
      setEditValue(fileName); // Reset to original if empty
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(fileName);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleOpenModal = () => {
    setTempDocGroup(docGroup);
    setTempDocType(docType);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTempDocGroup(docGroup);
    setTempDocType(docType);
  };

  const handleSaveDocGroupType = () => {
    if (onUpdateDocGroupType) {
      onUpdateDocGroupType(tempDocGroup, tempDocType);
    }
    setIsModalOpen(false);
  };

  const hasDocGroupType = docGroup || docType;

  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          gap: 0.4,
          borderRadius: "8px",
          display: "flex",
          padding: "4px 5px",
          border: "1px solid #dde0e4",
          background: `${background} !important`,
          color: `${color} !important`,
          fontSize: "10px !important",
          position: "relative",
          paddingLeft: imagePreview ? "40px" : undefined,
          minHeight: imagePreview ? "32px" : undefined,
          ".MuiButton-icon": {
            mx: 0.2,
          },
          ".MuiSvgIcon-root": {
            fontSize: "14px !important",
          },
        }}
        color="gray"
      >
        {!imagePreview ? icon : null}

        <ImagePreviewBox imagePreview={imagePreview} />

        {isEditing ? (
          <TextField
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              style: {
                fontSize: "10px",
                fontWeight: 400,
                lineHeight: 1.75,
                padding: 0,
                margin: 0,
                color: "inherit",
              },
            }}
            inputProps={{
              style: {
                padding: 0,
                fontSize: "10px",
                fontWeight: 400,
                lineHeight: 1.75,
                color: "inherit",
              },
            }}
            sx={{
              maxWidth: "200px",
              mr: 1,
              "& .MuiInputBase-input": {
                padding: 0,
                fontSize: "10px",
                fontWeight: 400,
                lineHeight: 1.75,
                color: "inherit",
                background: "white",
                borderRadius: "2px",
                px: 0.5,
                border: `1px solid ${color}`,
              },
            }}
          />
        ) : (
          <Tooltip title="Click to edit name">
            <Typography
              onClick={handleStartEdit}
              sx={{
                maxWidth: "200px",
                overflow: "hidden",
                cursor: "pointer",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 400,
                lineHeight: 1.75,
                fontSize: "10px",
                mr: 1,
                "&:hover": {
                  textDecoration: "underline",
                  textDecorationStyle: "dotted",
                  textUnderlineOffset: "3px",
                },
              }}
            >
              {fileName}
            </Typography>
          </Tooltip>
        )}

        {/* Doc Group/Type indicator and edit button */}
        {enableDocGroupTypeEdit && (
          <Tooltip
            title={
              hasDocGroupType
                ? `${docGroup || ""} ${docType ? `> ${docType}` : ""}`
                : "Set document type"
            }
          >
            <IconButton
              onClick={handleOpenModal}
              sx={{
                p: 0,
                color: "inherit",
                ml: 0.5,
                mr: 0.5,
              }}
              size="small"
            >
              {hasDocGroupType ? (
                <FolderOutlined className="thin" />
              ) : (
                <EditOutlined className="thin" />
              )}
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Remove">
            <IconButton
              onClick={onDelete}
              sx={{ p: 0, color: "inherit" }}
              size="small"
            >
              <CloseRounded className="thin" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Document Group/Type Selection Modal */}
      <BasicModal
        title={`Categorize File`}
        show={isModalOpen}
        maxWidth="xs"
        titleBottomContent={
          <Flx ac sx={{ mt: 0.5 }}>
            <AttachFileOutlined
              className="thin"
              sx={{ mr: 0.2, fontSize: "13px" }}
            />
            <Txt secondary>{fileName}</Txt>
          </Flx>
        }
        onClose={handleCloseModal}
      >
        <SelectFileAssignmentInput
          placeholder="Search document categories and types..."
          onChange={(params) => {
            setTempDocGroup(params?.docGroup);
            setTempDocType(params?.docType);
          }}
          docGroup={tempDocGroup}
          docType={tempDocType}
          sx={{ mb: 3 }}
        />
        {/* <InputWrapper label={"Select File Category and Type"}>
          <SelectFileAssignmentInput
            placeholder="Search document categories and types..."
            onChange={(params) => {
              setTempDocGroup(params?.docGroup);
              setTempDocType(params?.docType);
            }}
            docGroup={tempDocGroup}
            docType={tempDocType}
            sx={{ mb: 3 }}
          />
        </InputWrapper> */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleCloseModal} variant="outlined" size="small">
            Cancel
          </Button>
          <Button
            onClick={handleSaveDocGroupType}
            variant="contained"
            size="small"
          >
            Save
          </Button>
        </Box>
      </BasicModal>
    </>
  );
};

const ImagePreviewBox = ({ imagePreview }) => {
  if (isNil(imagePreview)) {
    return null;
  }
  return (
    <Box
      sx={{
        position: "absolute",
        left: 4,
        top: "50%",
        transform: "translateY(-50%)",
        width: 24,
        height: 24,
        borderRadius: "4px",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        background: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <img
        src={imagePreview}
        alt=""
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "cover",
        }}
      />
    </Box>
  );
};

export default RenderedPreviewUploadedFiles;
